import React, { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Button,
  message,
  Space,
  Typography,
  Switch,
  Radio,
  DatePicker,
  Alert,
  Divider,
  Row,
  Col,
  Tag,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  DisconnectOutlined,
  CheckCircleOutlined,
  StopOutlined,
  CalendarOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

export default function SandwichRuleSettings() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Settings State
  const [active, setActive] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState(null);

  // Weekly Off
  const [applyToWeeklyOff, setApplyToWeeklyOff] = useState(true);
  const [woTriggerAbsent, setWoTriggerAbsent] = useState(true);
  const [woTriggerPaidLeave, setWoTriggerPaidLeave] = useState(true);
  const [woTriggerMixed, setWoTriggerMixed] = useState(true);

  // Holiday
  const [applyToHoliday, setApplyToHoliday] = useState(true);
  const [holidayTriggerAbsent, setHolidayTriggerAbsent] = useState(true);
  const [holidayTriggerPaidLeave, setHolidayTriggerPaidLeave] = useState(true);
  const [holidayTriggerMixed, setHolidayTriggerMixed] = useState(true);

  // Consecutive policy
  const [consecutiveOffPolicy, setConsecutiveOffPolicy] = useState('deduct_all');

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/admin/settings/sandwich-rule');
      const s = resp?.data?.settings || {};
      setActive(!!s.active);
      setEffectiveFrom(s.effectiveFrom ? dayjs(s.effectiveFrom) : null);
      setApplyToWeeklyOff(s.applyToWeeklyOff !== false);
      setWoTriggerAbsent(s.woTriggerAbsent !== false);
      setWoTriggerPaidLeave(s.woTriggerPaidLeave !== false);
      setWoTriggerMixed(s.woTriggerMixed !== false);
      setApplyToHoliday(s.applyToHoliday !== false);
      setHolidayTriggerAbsent(s.holidayTriggerAbsent !== false);
      setHolidayTriggerPaidLeave(s.holidayTriggerPaidLeave !== false);
      setHolidayTriggerMixed(s.holidayTriggerMixed !== false);
      setConsecutiveOffPolicy(s.consecutiveOffPolicy || 'deduct_all');
    } catch (e) {
      message.error('Failed to load sandwich rule settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        active,
        effectiveFrom: effectiveFrom ? effectiveFrom.format('YYYY-MM-DD') : null,
        applyToWeeklyOff,
        woTriggerAbsent,
        woTriggerPaidLeave,
        woTriggerMixed,
        applyToHoliday,
        holidayTriggerAbsent,
        holidayTriggerPaidLeave,
        holidayTriggerMixed,
        consecutiveOffPolicy,
      };

      const resp = await api.put('/admin/settings/sandwich-rule', payload);
      if (resp?.data?.success) {
        message.success('Sandwich rule settings saved successfully!');
      } else {
        message.error(resp?.data?.message || 'Failed to save settings');
      }
    } catch (e) {
      console.error(e);
      message.error('Failed to save sandwich rule settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, minHeight: '100vh', transition: 'margin-left 0.2s', background: '#f0f2f5' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Sandwich Rule Settings" />
        <Content style={{ padding: '16px 20px 40px', overflowY: 'auto' }}>
          {/* Header Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <Space align="center" size="middle">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/settings')}
                  style={{ borderRadius: 6 }}
                >
                  Back to Settings
                </Button>
                <div>
                  <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DisconnectOutlined style={{ color: '#1890ff' }} /> Sandwich Rule Settings
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Configure sandwich deduction policy for weekly offs and holidays on consecutive absences or leaves
                  </Text>
                </div>
              </Space>

              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                size="large"
                style={{ borderRadius: 6, fontWeight: 600, paddingLeft: 24, paddingRight: 24 }}
              >
                Save Settings
              </Button>
            </div>

          {/* Master Enable Card */}
          <Card
            bordered={false}
            style={{
              marginBottom: 20,
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              borderLeft: active ? '5px solid #52c41a' : '5px solid #d9d9d9',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Text strong style={{ fontSize: 16 }}>
                    Enable Sandwich Rule
                  </Text>
                  {active ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      ACTIVE IN PAYROLL
                    </Tag>
                  ) : (
                    <Tag color="default" icon={<StopOutlined />}>
                      DISABLED (Standard Offs Paid)
                    </Tag>
                  )}
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                  Jab ye rule ON hoga, tabhi payroll calculation me sandwich deductions lagu honge. OFF hone par koi deduction nahi hoga.
                </Text>
              </div>
              <Switch
                checked={active}
                onChange={setActive}
                checkedChildren="ON"
                unCheckedChildren="OFF"
                style={{ transform: 'scale(1.2)' }}
              />
            </div>
          </Card>

          {/* How It Works Guide */}
          <Card
            bordered={false}
            style={{
              marginBottom: 20,
              borderRadius: 8,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <BulbOutlined style={{ color: '#f59e0b', fontSize: 18 }} />
              <Text strong style={{ fontSize: 14 }}>
                Sandwich Rule Explanation & Examples:
              </Text>
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                  <Text strong style={{ color: '#dc2626' }}>
                    1. Absent Sandwich (Sat Absent + Mon Absent)
                  </Text>
                  <Paragraph style={{ margin: '6px 0 0 0', fontSize: 12.5, color: '#4b5563' }}>
                    Agar employee kisi Weekly Off / Holiday ke dono taraf <strong>Absent</strong> hai, to beech ka Weekly Off / Holiday bhi <strong>Unpaid (0 Pay)</strong> hoga. Teeno din (Sat, Sun, Mon) ka salary deduct hoga.
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                  <Text strong style={{ color: '#2563eb' }}>
                    2. Paid Leave Sandwich (Sat Leave + Mon Leave)
                  </Text>
                  <Paragraph style={{ margin: '6px 0 0 0', fontSize: 12.5, color: '#4b5563' }}>
                    Agar employee kisi Weekly Off / Holiday ke dono taraf <strong>Paid Leave</strong> leta hai, to Sat aur Mon ka paisa leave balance se milega, lekin beech ke <strong>Weekly Off / Holiday ka paisa NAHI milega (Unpaid)</strong>.
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                  <Text strong style={{ color: '#7c3aed' }}>
                    3. Mid-Week Holidays (Tue - Wed - Thu)
                  </Text>
                  <Paragraph style={{ margin: '6px 0 0 0', fontSize: 12.5, color: '#4b5563' }}>
                    Ye rule hafte ke kisi bhi din kaam karega. Jaise Wednesday ko Diwali holiday hai, aur Tuesday & Thursday dono absent hain, to Wednesday holiday unpaid ho jayegi.
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb' }}>
                  <Text strong style={{ color: '#059669' }}>
                    4. Lagatar Chhuttiyan / 2-Day Clusters
                  </Text>
                  <Paragraph style={{ margin: '6px 0 0 0', fontSize: 12.5, color: '#4b5563' }}>
                    Agar Saturday aur Sunday dono Weekly Off hain, ya Holiday + Weekly Off lagatar hain, to poora block check hoga aur standard policy ke hisab se dono din deduct honge.
                  </Paragraph>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Rule Configuration Settings */}
          <div style={{ opacity: active ? 1 : 0.6, pointerEvents: active ? 'auto' : 'none', transition: 'all 0.3s' }}>
            {/* Weekly Off Settings */}
            <Card
              title={
                <Space>
                  <CalendarOutlined style={{ color: '#1890ff' }} />
                  <span>Weekly Off Sandwich Policy</span>
                </Space>
              }
              bordered={false}
              style={{ marginBottom: 20, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <Text strong>Apply Sandwich Rule to Weekly Offs</Text>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    Weekly Offs (jaise Sunday ya rostered offs) par sandwich rule enforce karein
                  </Text>
                </div>
                <Switch checked={applyToWeeklyOff} onChange={setApplyToWeeklyOff} />
              </div>

              {applyToWeeklyOff && (
                <div style={{ background: '#fafafa', padding: '14px 16px', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                    Weekly Off Trigger Conditions (Kaise lagu hoga):
                  </Text>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb', height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 13, color: '#dc2626' }}>Trigger on Absents</Text>
                          <Switch checked={woTriggerAbsent} onChange={setWoTriggerAbsent} size="small" />
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: '#4b5563', lineHeight: 1.6 }}>
                          <li>Sat Absent + Mon Absent hone par <strong>Sunday Weekly Off</strong> ka paisa katega.</li>
                          <li>Sat, Sun, Mon teeno din <strong>Unpaid (₹0)</strong> rahenge.</li>
                        </ul>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb', height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 13, color: '#2563eb' }}>Trigger on Paid Leaves</Text>
                          <Switch checked={woTriggerPaidLeave} onChange={setWoTriggerPaidLeave} size="small" />
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: '#4b5563', lineHeight: 1.6 }}>
                          <li>Sat Paid Leave + Mon Paid Leave hone par <strong>Sunday Weekly Off</strong> ka paisa katega.</li>
                          <li>Sat aur Mon leave balance se paid honge, lekin Sunday <strong>Unpaid (₹0)</strong> rahega.</li>
                        </ul>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb', height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 13, color: '#7c3aed' }}>Trigger on Mixed</Text>
                          <Switch checked={woTriggerMixed} onChange={setWoTriggerMixed} size="small" />
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: '#4b5563', lineHeight: 1.6 }}>
                          <li>Ek din Absent aur ek din Paid Leave hone par (e.g. Sat Absent + Mon Leave).</li>
                          <li>Beech ka <strong>Sunday Weekly Off</strong> sandwich hokar <strong>Unpaid (₹0)</strong> rahega.</li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </Card>

            {/* Holiday Settings */}
            <Card
              title={
                <Space>
                  <CalendarOutlined style={{ color: '#722ed1' }} />
                  <span>Holiday & National Holiday Sandwich Policy</span>
                </Space>
              }
              bordered={false}
              style={{ marginBottom: 20, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <Text strong>Apply Sandwich Rule to Holidays</Text>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    Festival aur National Holidays par sandwich rule enforce karein
                  </Text>
                </div>
                <Switch checked={applyToHoliday} onChange={setApplyToHoliday} />
              </div>

              {applyToHoliday && (
                <div style={{ background: '#fafafa', padding: '14px 16px', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
                    Holiday Trigger Conditions (Kaise lagu hoga):
                  </Text>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb', height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 13, color: '#dc2626' }}>Trigger on Absents</Text>
                          <Switch checked={holidayTriggerAbsent} onChange={setHolidayTriggerAbsent} size="small" />
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: '#4b5563', lineHeight: 1.6 }}>
                          <li>Holiday ke aage aur peechhe Absent hone par (e.g. Tue Absent + Thu Absent).</li>
                          <li>Beech ki <strong>Holiday / Festival Off</strong> ka paisa nahi milega (Unpaid ₹0).</li>
                        </ul>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb', height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 13, color: '#2563eb' }}>Trigger on Paid Leaves</Text>
                          <Switch checked={holidayTriggerPaidLeave} onChange={setHolidayTriggerPaidLeave} size="small" />
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: '#4b5563', lineHeight: 1.6 }}>
                          <li>Holiday ke aage aur peechhe Paid Leave lene par (e.g. Tue Leave + Thu Leave).</li>
                          <li>Dono din leave balance se paid honge, lekin <strong>Holiday Unpaid (₹0)</strong> ho jayegi.</li>
                        </ul>
                      </div>
                    </Col>
                    <Col xs={24} md={8}>
                      <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: 6, border: '1px solid #e5e7eb', height: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 13, color: '#7c3aed' }}>Trigger on Mixed</Text>
                          <Switch checked={holidayTriggerMixed} onChange={setHolidayTriggerMixed} size="small" />
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, color: '#4b5563', lineHeight: 1.6 }}>
                          <li>Ek din Absent aur ek din Paid Leave hone par (e.g. Tue Absent + Thu Leave).</li>
                          <li>Beech ki <strong>Holiday Unpaid (₹0)</strong> ho jayegi.</li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </Card>

            {/* Consecutive Days Cluster Handling */}
            <Card
              title="Consecutive Non-Working Days Handling (2 Ya Zyada Lagatar Chhuttiyan)"
              bordered={false}
              style={{ marginBottom: 20, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
            >
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Jab 2 din lagatar weekly off ya holiday hon (jaise Saturday + Sunday off, ya Holiday + Weekly Off), to sandwich hone par kitne din deduct hon:
              </Text>

              <Radio.Group
                value={consecutiveOffPolicy}
                onChange={(e) => setConsecutiveOffPolicy(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                  <Radio value="deduct_all">
                    <div>
                      <Text strong>Deduct All Consecutive Off Days (Recommended / Standard)</Text>
                      <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                        Lagatar aane wale saare offs/holidays deduct honge (e.g. Sat + Sun dono deduct honge).
                      </Text>
                    </div>
                  </Radio>
                  <Radio value="deduct_first_only">
                    <div>
                      <Text strong>Deduct Only First Off Day</Text>
                      <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                        Lagatar chhuttiyon me se sirf pehli chhutti deduct hogi.
                      </Text>
                    </div>
                  </Radio>
                  <Radio value="deduct_last_only">
                    <div>
                      <Text strong>Deduct Only Last Off Day</Text>
                      <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                        Lagatar chhuttiyon me se sirf aakhri chhutti deduct hogi.
                      </Text>
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </Card>

            {/* Effective Date Card */}
            <Card
              title="Effective Date (Kab Se Lagu Karna Hai)"
              bordered={false}
              style={{ marginBottom: 20, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <Text strong>Rule Effective From Date</Text>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    Is date se pehle ke payroll par ye rule lagu nahi hoga (Purane months safe rahenge)
                  </Text>
                </div>
                <DatePicker
                  value={effectiveFrom}
                  onChange={setEffectiveFrom}
                  placeholder="Select Effective Date"
                  format="DD-MM-YYYY"
                  style={{ width: 200 }}
                />
              </div>
            </Card>
          </div>
        </Content>
    </Layout>
  </Layout>
  );
}
